import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PlusCircle, Target, Mic, ArrowRight, Sparkles, Check } from "lucide-react";

const STEPS = [
  {
    key: "expense",
    accent: "#EF4444",
    accentSoft: "#FEE2E2",
    icon: PlusCircle,
    eyebrow: "Paso 1 de 3",
    title: "Registra tu primer gasto",
    description:
      "Anota cuánto, en qué categoría y la fecha. Te toma menos de 10 segundos y ya verás tu saldo del mes en vivo.",
    cta: "Agregar gasto ahora",
    testId: "welcome-step-expense",
  },
  {
    key: "budget",
    accent: "#0052FF",
    accentSoft: "#DBEAFE",
    icon: Target,
    eyebrow: "Paso 2 de 3",
    title: "Define un presupuesto",
    description:
      "Pon un tope mensual a las categorías que más se te van (Comida, Transporte…). Finly+ te avisa cuando te acercas al límite.",
    cta: "Crear presupuesto",
    testId: "welcome-step-budget",
  },
  {
    key: "voice",
    accent: "#DC2626",
    accentSoft: "#FEE2E2",
    icon: Mic,
    eyebrow: "Paso 3 de 3",
    title: "Prueba la captura por voz",
    description:
      "Di algo como “Gasté 250 pesos en Uber ayer” y Finly+ lo registra solo. Es la forma más rápida de no olvidar nada.",
    cta: "Probar voz",
    testId: "welcome-step-voice",
  },
];

export default function WelcomeModal({
  open,
  onOpenChange,
  onAddExpense,
  onCreateBudget,
  onTryVoice,
  onComplete,
}) {
  const [stepIdx, setStepIdx] = useState(0);
  const step = STEPS[stepIdx];
  const Icon = step.icon;

  const closeAndMark = () => {
    onComplete?.();
    onOpenChange(false);
    setStepIdx(0);
  };

  const handleSkip = () => {
    closeAndMark();
  };

  const handleAction = () => {
    const cb = step.key === "expense" ? onAddExpense : step.key === "budget" ? onCreateBudget : onTryVoice;
    onComplete?.();
    onOpenChange(false);
    setStepIdx(0);
    // defer so dialog close animation doesn't race with the next action
    setTimeout(() => cb?.(), 150);
  };

  const handleNext = () => {
    if (stepIdx < STEPS.length - 1) {
      setStepIdx((i) => i + 1);
    } else {
      closeAndMark();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) closeAndMark(); else onOpenChange(true); }}>
      <DialogContent
        className="bg-[#131C2E] max-w-md p-0 overflow-hidden"
        data-testid="welcome-modal"
      >
        {/* Decorative header strip */}
        <div
          className="px-6 pt-6 pb-5"
          style={{
            background: `linear-gradient(135deg, ${step.accentSoft} 0%, #ffffff 90%)`,
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <span
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em]"
              style={{ backgroundColor: "#ffffff", color: step.accent, boxShadow: "0 1px 0 rgba(15,23,42,0.04)" }}
              data-testid="welcome-step-eyebrow"
            >
              <Sparkles className="w-3 h-3" />
              {step.eyebrow}
            </span>
          </div>

          <div className="flex items-start gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: "#ffffff", boxShadow: `0 6px 16px ${step.accent}25` }}
            >
              <Icon className="w-6 h-6" style={{ color: step.accent }} />
            </div>
            <DialogHeader className="text-left space-y-1.5 flex-1">
              <DialogTitle
                className="font-display text-2xl text-[#F1F5F9] leading-tight"
                data-testid="welcome-step-title"
              >
                {step.title}
              </DialogTitle>
              <DialogDescription className="text-sm text-[#475569] leading-relaxed">
                {step.description}
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        {/* Body / progress + actions */}
        <div className="px-6 pb-6 pt-4 space-y-5">
          {/* Step progress dots */}
          <div className="flex items-center justify-center gap-2" data-testid="welcome-progress">
            {STEPS.map((s, i) => {
              const done = i < stepIdx;
              const active = i === stepIdx;
              return (
                <span
                  key={s.key}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: active ? 28 : 10,
                    backgroundColor: done ? step.accent : active ? step.accent : "#E2E8F0",
                    opacity: done ? 0.55 : 1,
                  }}
                />
              );
            })}
          </div>

          <div className="flex flex-col gap-2">
            <Button
              type="button"
              onClick={handleAction}
              className="w-full h-12 text-white rounded-xl font-semibold transition-all hover:-translate-y-0.5"
              style={{ backgroundColor: step.accent, boxShadow: `0 10px 20px -8px ${step.accent}` }}
              data-testid={`${step.testId}-primary`}
            >
              {step.cta}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              onClick={handleNext}
              className="w-full h-10 text-[#64748B] hover:text-[#F1F5F9] hover:bg-[#1B2638] rounded-xl font-medium"
              data-testid={`${step.testId}-next`}
            >
              {stepIdx < STEPS.length - 1 ? (
                <>Siguiente paso</>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-1.5" /> Listo, ya exploro por mi cuenta
                </>
              )}
            </Button>
          </div>

          <button
            type="button"
            onClick={handleSkip}
            className="block mx-auto text-xs text-[#94A3B8] hover:text-[#64748B] underline-offset-2 hover:underline"
            data-testid="welcome-skip"
          >
            Saltar tour
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
